/**
 * WHAT HAPPENS TO THE FOLLOWERS WHEN A LEADER LOSES A VARIANT.
 *
 * Two effects, and they are one decision rather than two, which is why they are computed
 * together and committed as a single undo op:
 *
 *  1. **Every follower's row for a deleted variant goes.** `syncScenarioBranches` deliberately
 *     KEEPS orphaned rows rather than dropping them — dropping would destroy an authored answer
 *     key because somebody deleted a variant — but a row whose variant is gone is unreachable:
 *     the pager is driven by the leader's bank and every lookup is by id. Left alone they
 *     accumulate invisibly, so the delete takes them.
 *  2. **Below two variants the block stops leading, so the followers stop following.** A
 *     one-variant bank is not a scenario, and a follower pointing at it had a pager with one
 *     entry and no way back except noticing and unlinking by hand.
 *
 * Pulled out of the page so it can be tested as what it is — a pure function from "what the
 * leader looked like before and after" to "what each follower should now hold".
 */
import { collapseScenarioFollower } from '@agavi/blocks';

import { withScenarioRowRemoved } from './scenario-links';

/** One follower's before and after, ready to become an `update-blocks` entry. */
export interface FollowerPrune {
  blockId: string;
  prev: Record<string, unknown>;
  next: Record<string, unknown>;
}

type PrunableBlock = { id: string; type: string; data?: unknown };

const variantIdsOf = (d: unknown) =>
  ((d as { versions?: { id: string }[] } | undefined)?.versions ?? []).map(v => v.id);

/**
 * `before`/`after` are the leader's own data either side of the edit; `blocks` is the task.
 *
 * Returns only the followers that actually change, so an empty result means the edit was not a
 * variant deletion and the caller can take its ordinary single-block path.
 */
export function pruneFollowers(
  leaderId: string, before: unknown, after: Record<string, unknown>, blocks: PrunableBlock[],
  /** The variant the teacher deleted, when this write was a deletion — see below. */
  removedVersionId?: string,
): FollowerPrune[] {
  const had = variantIdsOf(before);
  const left = variantIdsOf(after);
  const gone = had.filter(vid => !left.includes(vid));
  if (gone.length === 0) return [];
  // Read from AFTER, not from the count of what went: a bank can lose a variant and still lead.
  const collapses = left.length < 2;

  /*
   * WHICH VARIANT SURVIVED, which is not the same question as "which ids are missing".
   *
   * Dropping to one variant collapses the bank: the survivor's content is absorbed into the
   * block and `versions` is cleared, so `gone` names BOTH and the survivor is missing too. The
   * teacher's deletion is the only thing that distinguishes them, and `removedVersionId` is the
   * delete telling us. Without it — a paste, an undo, some other write that happens to clear a
   * bank — there is no survivor to name and the followers unlink without inheriting an answer,
   * which is the honest outcome for a change nobody made variant by variant.
   */
  const survivor = removedVersionId ? had.find(vid => vid !== removedVersionId) : undefined;
  // Only the variant actually deleted loses its row. On a collapse the survivor's row has to
  // outlive the prune, because it is the answer the block is about to adopt.
  const prune = removedVersionId ? gone.filter(vid => vid !== survivor) : gone;

  const out: FollowerPrune[] = [];
  for (const b of blocks) {
    if ((b.data as { followsBlockId?: string } | undefined)?.followsBlockId !== leaderId) continue;
    let next = b.data as Record<string, unknown>;
    for (const vid of prune) next = withScenarioRowRemoved(next, vid);
    if (collapses) next = collapseScenarioFollower(b.type, next, survivor);
    if (next !== b.data) out.push({ blockId: b.id, prev: b.data as Record<string, unknown>, next });
  }
  return out;
}
